import { NetworkStatus, NetworkDiagnostics } from '@/hooks/useNetworkStatus';

export interface RetryConfig {
  maxRetries: number;
  baseDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  exponentialBackoff: boolean;
  retryOnStatus?: number[]; // HTTP status codes to retry on
  retryOnNetworkError: boolean;
  timeoutMs: number;
}

export interface NetworkRecoveryOptions {
  retryConfig?: Partial<RetryConfig>;
  enableResumption?: boolean;
  progressCallback?: (progress: { loaded: number; total?: number; speed?: number }) => void;
  networkStatusCallback?: (status: NetworkStatus) => void;
  onNetworkRecovered?: () => void;
  onNetworkLost?: () => void;
}

export interface DownloadAttempt {
  attempt: number;
  startTime: Date;
  endTime?: Date;
  error?: Error;
  networkStatus?: NetworkStatus;
  bytesDownloaded?: number;
  success?: boolean;
}

export interface NetworkRecoveryResult<T = any> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: DownloadAttempt[];
  totalDuration: number;
  finalNetworkStatus?: NetworkStatus;
  recoveryActions?: string[];
}

export type NetworkRecoveryFunction<T = any> = (
  signal?: AbortSignal,
  onProgress?: (progress: { loaded: number; total?: number }) => void
) => Promise<T>;

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 30000, // 30 seconds
  exponentialBackoff: true,
  retryOnStatus: [408, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524],
  retryOnNetworkError: true,
  timeoutMs: 30000 // 30 seconds per attempt
};

export class NetworkRecoveryManager {
  private retryConfig: RetryConfig;
  private currentNetworkStatus?: NetworkStatus;
  private networkDiagnostics?: NetworkDiagnostics;
  private resumptionData: Map<string, { bytes: number; etag?: string; lastModified?: string }> = new Map();

  constructor(private options: NetworkRecoveryOptions = {}) {
    this.retryConfig = { ...DEFAULT_RETRY_CONFIG, ...options.retryConfig };
  }

  /**
   * Update network status for intelligent retry decisions
   */
  updateNetworkStatus(status: NetworkStatus, diagnostics?: NetworkDiagnostics) {
    const wasOffline = this.currentNetworkStatus?.isOnline === false;
    const isNowOnline = status.isOnline;

    this.currentNetworkStatus = status;
    this.networkDiagnostics = diagnostics;

    // Trigger callbacks
    this.options.networkStatusCallback?.(status);

    // Network recovery callback
    if (wasOffline && isNowOnline) {
      this.options.onNetworkRecovered?.();
    } else if (!wasOffline && !isNowOnline) {
      this.options.onNetworkLost?.();
    }
  }

  /**
   * Execute a network operation with intelligent retry and recovery
   */
  async executeWithRecovery<T>(
    operation: NetworkRecoveryFunction<T>,
    operationId?: string
  ): Promise<NetworkRecoveryResult<T>> {
    const startTime = Date.now();
    const attempts: DownloadAttempt[] = [];
    const recoveryActions: string[] = [];

    let lastError: Error | undefined;
    let abortController: AbortController | undefined;

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      const attemptStart = new Date();
      
      try {
        // Create new abort controller for this attempt
        abortController = new AbortController();
        
        // Set timeout for this attempt
        const timeoutId = setTimeout(() => {
          abortController?.abort();
        }, this.retryConfig.timeoutMs);

        // Create progress handler
        const progressHandler = (progress: { loaded: number; total?: number }) => {
          this.options.progressCallback?.({
            ...progress,
            speed: this.calculateDownloadSpeed(progress.loaded, attemptStart)
          });
        };

        // Check network status before attempt
        if (this.currentNetworkStatus && !this.currentNetworkStatus.isOnline) {
          throw new Error('Network offline - waiting for connection');
        }

        // Adjust timeout based on network quality
        if (this.currentNetworkStatus?.connectionQuality === 'poor') {
          recoveryActions.push('Increased timeout for poor connection');
          clearTimeout(timeoutId);
          setTimeout(() => abortController?.abort(), this.retryConfig.timeoutMs * 2);
        }

        // Execute the operation
        const result = await operation(abortController.signal, progressHandler);
        
        clearTimeout(timeoutId);

        // Success!
        const attemptEnd = new Date();
        attempts.push({
          attempt,
          startTime: attemptStart,
          endTime: attemptEnd,
          networkStatus: { ...this.currentNetworkStatus } as NetworkStatus,
          success: true
        });

        return {
          success: true,
          data: result,
          attempts,
          totalDuration: Date.now() - startTime,
          finalNetworkStatus: this.currentNetworkStatus,
          recoveryActions
        };

      } catch (error) {
        const attemptEnd = new Date();
        const attemptError = error instanceof Error ? error : new Error(String(error));
        
        attempts.push({
          attempt,
          startTime: attemptStart,
          endTime: attemptEnd,
          error: attemptError,
          networkStatus: { ...this.currentNetworkStatus } as NetworkStatus,
          success: false
        });

        lastError = attemptError;

        // Determine if we should retry
        const shouldRetry = await this.shouldRetry(attemptError, attempt);
        
        if (!shouldRetry || attempt >= this.retryConfig.maxRetries) {
          break;
        }

        // Calculate delay for next attempt
        const delay = this.calculateRetryDelay(attempt);
        recoveryActions.push(`Retrying after ${delay}ms delay (attempt ${attempt + 1})`);

        // Apply recovery strategies
        await this.applyRecoveryStrategies(attemptError, attempt, recoveryActions);

        // Wait before retry
        await this.delay(delay);
      }
    }

    return {
      success: false,
      error: lastError,
      attempts,
      totalDuration: Date.now() - startTime,
      finalNetworkStatus: this.currentNetworkStatus,
      recoveryActions
    };
  }

  /**
   * Create a resumable fetch operation
   */
  createResumableDownload(url: string, operationId: string): NetworkRecoveryFunction<Response> {
    return async (signal?: AbortSignal, onProgress?: (progress: { loaded: number; total?: number }) => void) => {
      const resumption = this.resumptionData.get(operationId);
      const headers: HeadersInit = {};

      // Add resumption headers if available
      if (resumption && this.options.enableResumption) {
        if (resumption.bytes > 0) {
          headers['Range'] = `bytes=${resumption.bytes}-`;
        }
        if (resumption.etag) {
          headers['If-Range'] = resumption.etag;
        }
      }

      const response = await fetch(url, {
        headers,
        signal,
        cache: 'no-cache'
      });

      if (!response.ok) {
        // Handle range request not satisfiable
        if (response.status === 416) {
          // File might have been completely downloaded or changed
          this.resumptionData.delete(operationId);
          throw new Error('Range not satisfiable - restarting download');
        }
        
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Store resumption data for next attempt if needed
      const contentLength = response.headers.get('content-length');
      const etag = response.headers.get('etag');
      const lastModified = response.headers.get('last-modified');

      if (response.status === 206) { // Partial content
        // This is a resumed download
        if (onProgress && contentLength) {
          const remaining = parseInt(contentLength);
          const resumedFrom = resumption?.bytes || 0;
          onProgress({ loaded: resumedFrom, total: resumedFrom + remaining });
        }
      }

      // Wrap response with progress tracking
      if (onProgress) {
        return this.wrapResponseWithProgress(response, onProgress, operationId, {
          etag: etag || undefined,
          lastModified: lastModified || undefined
        });
      }

      return response;
    };
  }

  /**
   * Determine if operation should be retried based on error
   */
  private async shouldRetry(error: Error, attempt: number): Promise<boolean> {
    if (attempt >= this.retryConfig.maxRetries) {
      return false;
    }

    const errorMessage = error.message.toLowerCase();

    // Always retry on network errors if enabled
    if (this.retryConfig.retryOnNetworkError) {
      if (errorMessage.includes('network') ||
          errorMessage.includes('timeout') ||
          errorMessage.includes('fetch') ||
          errorMessage.includes('abort') ||
          errorMessage.includes('offline')) {
        return true;
      }
    }

    // Retry on specific HTTP status codes
    const statusMatch = errorMessage.match(/http (\d+)/);
    if (statusMatch) {
      const status = parseInt(statusMatch[1]);
      return this.retryConfig.retryOnStatus?.includes(status) ?? false;
    }

    // Don't retry on other errors
    return false;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number): number {
    if (!this.retryConfig.exponentialBackoff) {
      return this.retryConfig.baseDelay;
    }

    const exponentialDelay = this.retryConfig.baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.1 * exponentialDelay; // Add 10% jitter
    const delayWithJitter = exponentialDelay + jitter;

    return Math.min(delayWithJitter, this.retryConfig.maxDelay);
  }

  /**
   * Apply recovery strategies based on error type and network status
   */
  private async applyRecoveryStrategies(error: Error, attempt: number, recoveryActions: string[]): Promise<void> {
    const errorMessage = error.message.toLowerCase();

    // Network offline - wait for connection
    if (!this.currentNetworkStatus?.isOnline) {
      recoveryActions.push('Network offline - waiting for connection recovery');
      
      // Wait for network to come back online (with timeout)
      const waitStart = Date.now();
      const maxWait = 30000; // 30 seconds
      
      while (!this.currentNetworkStatus?.isOnline && (Date.now() - waitStart) < maxWait) {
        await this.delay(1000);
      }
      
      if (this.currentNetworkStatus?.isOnline) {
        recoveryActions.push('Network connection recovered');
      }
    }

    // Poor connection quality - adjust strategy
    if (this.currentNetworkStatus?.connectionQuality === 'poor') {
      recoveryActions.push('Adjusting strategy for poor connection');
      
      // Wait longer for poor connections
      const poorConnectionDelay = this.calculateRetryDelay(attempt) * 1.5;
      await this.delay(poorConnectionDelay);
    }

    // DNS errors - try different endpoints
    if (errorMessage.includes('dns') || errorMessage.includes('name resolution')) {
      recoveryActions.push('DNS error detected - implementing DNS recovery');
      
      // Could switch to alternative endpoints or wait for DNS to recover
      await this.delay(5000); // Give DNS time to recover
    }

    // Rate limiting - respect server limits
    if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
      recoveryActions.push('Rate limit detected - implementing backoff');
      
      const rateLimitDelay = Math.min(this.retryConfig.maxDelay, 60000); // Max 1 minute for rate limits
      await this.delay(rateLimitDelay);
    }
  }

  /**
   * Wrap fetch response with progress tracking and resumption support
   */
  private wrapResponseWithProgress(
    response: Response, 
    onProgress: (progress: { loaded: number; total?: number }) => void,
    operationId: string,
    metadata: { etag?: string; lastModified?: string }
  ): Response {
    if (!response.body) {
      return response;
    }

    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength) : undefined;
    let loaded = 0;

    const readable = new ReadableStream({
      start(controller) {
        const reader = response.body!.getReader();
        
        function pump(): Promise<void> {
          return reader.read().then(({ done, value }) => {
            if (done) {
              // Download complete - clean up resumption data
              this.resumptionData.delete(operationId);
              controller.close();
              return;
            }
            
            loaded += value.length;
            
            // Update resumption data
            this.resumptionData.set(operationId, {
              bytes: loaded,
              etag: metadata.etag,
              lastModified: metadata.lastModified
            });
            
            // Report progress
            onProgress({ loaded, total });
            
            controller.enqueue(value);
            return pump();
          });
        }
        
        return pump();
      }
    });

    return new Response(readable, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    });
  }

  /**
   * Calculate download speed in bytes per second
   */
  private calculateDownloadSpeed(bytesLoaded: number, startTime: Date): number {
    const elapsedSeconds = (Date.now() - startTime.getTime()) / 1000;
    return elapsedSeconds > 0 ? bytesLoaded / elapsedSeconds : 0;
  }

  /**
   * Delay utility with promise
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Clear resumption data for an operation
   */
  clearResumptionData(operationId: string): void {
    this.resumptionData.delete(operationId);
  }

  /**
   * Get current retry configuration
   */
  getRetryConfig(): RetryConfig {
    return { ...this.retryConfig };
  }

  /**
   * Update retry configuration
   */
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config };
  }
} 